import React, { useState, useEffect, useCallback } from "react";
import {
  Page,
  Card,
  Text,
  Button,
  Banner,
  Spinner,
  BlockStack,
  TextField,
  Select,
  Box,
  Divider,
} from "@shopify/polaris";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

const DEFAULT_TRYON_PROMPT = `Je bent een virtuele paskamer AI. De eerste afbeelding is een foto van een klant.
De tweede afbeelding is een kledingstuk/artikel.

Genereer een realistische afbeelding waarin de klant het kledingstuk draagt.
Behoud het gezicht, lichaamsbouw en houding van de klant.
Pas het kledingstuk natuurlijk aan op het lichaam van de klant.
Zorg voor realistische schaduwen, plooien en pasvorm.`;

const DEFAULT_STYLE_PROMPT = `Je bent een mode-assistent. De eerste afbeelding is een foto van een klant.
De tweede afbeelding is een kledingstuk.
Geef een gedetailleerde, aanmoedigende beschrijving van hoe dit kledingstuk
bij deze klant zou staan. Inclusief:
1. Hoe de stijl bij hen past
2. Kleuroverwegingen
3. Pasvorm observaties
4. Algemeen stijladvies
Houd de toon positief en professioneel. Schrijf in het Nederlands.`;

const MODEL_OPTIONS = [
  { label: "Gemini 3.1 Flash Image (aanbevolen - snel & betrouwbaar)", value: "gemini-3.1-flash-image-preview" },
  { label: "Gemini 3 Pro Image (premium - beste kwaliteit)", value: "gemini-3-pro-image-preview" },
  { label: "Gemini 2.5 Flash Image (vorige generatie)", value: "gemini-2.5-flash-image" },
];

export default function AiSettingsPage() {
  const { apiFetch } = useShop();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [tryOnPrompt, setTryOnPrompt] = useState("");
  const [stylePrompt, setStylePrompt] = useState("");
  const [model, setModel] = useState("gemini-3-pro-image-preview");

  const load = useCallback(async () => {
    try {
      const data = await apiFetch("/ai-settings");
      setTryOnPrompt(data.tryOnPrompt || "");
      setStylePrompt(data.stylePrompt || "");
      setModel(data.model || "gemini-3-pro-image-preview");
    } catch {
      setError(t("ai.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await apiFetch("/ai-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tryOnPrompt, stylePrompt, model }),
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError(t("ai.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleResetTryOn = () => setTryOnPrompt(DEFAULT_TRYON_PROMPT);
  const handleResetStyle = () => setStylePrompt(DEFAULT_STYLE_PROMPT);

  if (loading) {
    return (
      <Page title={t("ai.title")}>
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  return (
    <Page title={t("ai.title")} subtitle={t("ai.subtitle")}>
      <BlockStack gap="500">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {success && (
          <Banner tone="success" onDismiss={() => setSuccess(false)}>
            {t("ai.saved")}
          </Banner>
        )}

        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <Text variant="headingSm" as="h2">{t("ai.model")}</Text>
              <Select
                label={t("ai.modelDesc")}
                options={MODEL_OPTIONS}
                value={model}
                onChange={setModel}
              />
              <Banner tone="info">
                <Text variant="bodySm">
                  {t("ai.modelWarning")}
                </Text>
              </Banner>
            </BlockStack>
          </Box>
        </Card>

        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text variant="headingSm" as="h2">{t("ai.imagePrompt")}</Text>
                <Button variant="plain" onClick={handleResetTryOn}>{t("ai.imagePromptReset")}</Button>
              </div>
              <Text variant="bodySm" tone="subdued">
                {t("ai.imagePromptDesc")}
              </Text>
              <TextField
                label="Try-on prompt"
                labelHidden
                value={tryOnPrompt}
                onChange={setTryOnPrompt}
                multiline={8}
                placeholder={DEFAULT_TRYON_PROMPT}
                autoComplete="off"
                monospaced
              />
              <Banner tone="info">
                <Text variant="bodySm">
                  {t("ai.imagePromptTip")}
                </Text>
              </Banner>
            </BlockStack>
          </Box>
        </Card>

        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text variant="headingSm" as="h2">{t("ai.stylePrompt")}</Text>
                <Button variant="plain" onClick={handleResetStyle}>{t("ai.stylePromptReset")}</Button>
              </div>
              <Text variant="bodySm" tone="subdued">
                {t("ai.stylePromptDesc")}
              </Text>
              <TextField
                label="Stijladvies prompt"
                labelHidden
                value={stylePrompt}
                onChange={setStylePrompt}
                multiline={6}
                placeholder={DEFAULT_STYLE_PROMPT}
                autoComplete="off"
                monospaced
              />
            </BlockStack>
          </Box>
        </Card>

        <Divider />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <Button variant="primary" size="large" onClick={handleSave} loading={saving}>
            {t("common.save")}
          </Button>
        </div>
      </BlockStack>
    </Page>
  );
}
